/**
 * Human-readable error description for the auth endpoints. Maps a
 * zod-validated `PrototypeApiError` to copy that tells the user
 * exactly what they should do next (full email, not username;
 * password is case-sensitive; provider not configured on this env).
 * Extracted from authActions.ts to keep that file under the 220-line
 * architecture cap.
 */
import { PrototypeApiError } from "../apiClient";

interface ApiBody {
	error?: string;
	fields?: string[];
}

export type AuthContext = "login" | "register";

/**
 * Map an error thrown from the auth endpoints to a human-readable
 * message that explains *what the user should do*, instead of leaking
 * zod's internal `origin / code / format / path` shape. The server
 * still gets the structured `validation_failed` payload; this is
 * purely the client toast copy.
 *
 * `context` distinguishes login vs register so the copy matches the
 * action the user was performing (e.g. "Registration failed" vs
 * "Sign-in failed", or "already registered" on 409 which only makes
 * sense for registration).
 */
export function describeAuthError(error: unknown, context: AuthContext = "login"): string {
	const fallback = context === "register" ? "Registration failed. Please try again." : "Sign-in failed. Please try again.";

	if (!(error instanceof PrototypeApiError)) {
		return fallback;
	}
	// Email format / shape rejection: the user almost certainly typed
	// the user-id (u_…) or some other non-email string into the
	// email field. Tell them exactly what the server wants so they
	// don't keep staring at "Invalid email address".
	if (error.status === 400) {
		const body = (error.body ?? {}) as { error?: string; fields?: string[] };
		if (body.error === "validation_failed" && body.fields?.includes("email")) {
			return "Please enter your full email address, not your username.";
		}
		if (body.error === "validation_failed" && body.fields?.includes("password")) {
			return "Password must be 8+ characters with at least one uppercase letter, one lowercase letter, and one number.";
		}
	}
	if (error.status === 401) {
		const body = (error.body ?? {}) as ApiBody;
		if (body.error === "invalid_credentials") {
			return "Email or password is incorrect. Try again or use Forgot password.";
		}
	}
	// Registration-specific: email already exists / display name already taken
	if (context === "register" && error.status === 409) {
		const body = (error.body ?? {}) as ApiBody;
		if (body.error === "display_name_taken") {
			return "That display name is already in use. Choose another one.";
		}
		return "This email is already registered. Try logging in instead.";
	}
	// Turnstile verification failure (shared by login + register)
	if (error.status === 403) {
		const body = (error.body ?? {}) as { error?: string };
		if (body.error === "turnstile_required" || body.error === "turnstile_failed") {
			return "Security verification failed. Please complete the check and try again.";
		}
		// Login-specific: unverified email
		if (context === "login" && body.error === "email_not_verified") {
			return "Please verify your email first. Check your inbox for the verification link.";
		}
	}
	if (error.status === 429) {
		return context === "register" ? "Too many attempts. Please wait a minute and try again." : "Too many sign-in attempts. Please wait a minute and try again.";
	}
	if (error.status === 0) {
		return "Network error. Check your connection and try again.";
	}
	return fallback;
}
